import { Box, Button, Grid, Stack, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import MedicationOutlinedIcon from "@mui/icons-material/MedicationOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import VerifiedOutlinedIcon from "@mui/icons-material/VerifiedOutlined";
import { useNavigate } from "react-router-dom";
import { mergeManagedContent } from "../../hooks/mergeManagedContent";
import { useLangPath } from "../../hooks/langAlias";
import { colors, eyebrowSx, fonts, radii, shadows } from "../../theme/designTokens";

// Splits the admin-managed About Us text into sentences so the hero
// can show a real excerpt instead of invented marketing copy.
function splitSentences(text) {
  if (!text) return [];
  const stripped = text.replace(/<[^>]*>/g, " ");
  const parts = stripped.split(/([.!?]+\s+)/);
  const sentences = [];
  for (let i = 0; i < parts.length; i += 2) {
    const sentence = ((parts[i] || "") + (parts[i + 1] || "")).trim();
    if (sentence) sentences.push(sentence);
  }
  return sentences;
}

const badges = [
  { icon: ShieldOutlinedIcon, label: "Licensed Pharmacy" },
  { icon: MedicationOutlinedIcon, label: "Genuine Medicines" },
  { icon: LocalShippingOutlinedIcon, label: "Fast Delivery" },
  { icon: AccessTimeOutlinedIcon, label: "24/7 Support" },
];

export default function Hero() {
  const navigate = useNavigate();
  const lp = useLangPath();

  const [langId, setLangId] = useState("");
  const [aboutSentences, setAboutSentences] = useState([]);

  const { managedContentSettings } = useSelector(
    (state) => state.managedContentSettings
  );
  const { activeLangFront } = useSelector((state) => state.miscFront);
  const { langs } = useSelector((state) => state.misc);

  useEffect(() => {
    if (activeLangFront) setLangId(activeLangFront._id);
  }, [activeLangFront]);

  useEffect(() => {
    if (
      managedContentSettings &&
      managedContentSettings.length > 0 &&
      langId !== ""
    ) {
      const M = mergeManagedContent(managedContentSettings, langId, langs);
      if (M && M.aboutUs && M.aboutUs.length > 0) {
        setAboutSentences(splitSentences(M.aboutUs[0].tabDetails));
      }
    }
  }, [langId, managedContentSettings]);

  const headline = aboutSentences[0] || "";
  const subheadline = aboutSentences.slice(1, 3).join(" ");

  const handleHowToOrder = () => {
    navigate(lp("/how-to-order"));
  };

  return (
    <Box
      sx={{
        display: { xs: "none", sm: "block" },
        background: `linear-gradient(180deg, ${colors.paperTint} 0%, ${colors.paper} 100%)`,
        py: { xs: 5, md: 8 },
      }}
    >
      <Box sx={{ maxWidth: 1200, mx: "auto", px: { xs: 2, sm: 3 }, overflow: "hidden" }}>
      <Grid container spacing={4} alignItems="center">
        <Grid item xs={12} md={6}>
          <Box sx={eyebrowSx}>Our Mission</Box>
          {headline && (
            <Typography
              sx={{
                fontFamily: fonts.display,
                fontWeight: 800,
                fontSize: { xs: "1.7rem", sm: "2.1rem", md: "2.3rem" },
                lineHeight: 1.2,
                color: colors.ink,
                mt: 2.25,
              }}
            >
              {headline}
            </Typography>
          )}
          {subheadline && (
            <Typography
              sx={{
                fontSize: { xs: "1rem", md: "1.05rem" },
                color: colors.ink2,
                lineHeight: 1.65,
                mt: 2.5,
                maxWidth: 560,
              }}
            >
              {subheadline}
            </Typography>
          )}
          <Stack direction="row" spacing={1.75} sx={{ mt: 3.5, flexWrap: "wrap", rowGap: 1.5 }}>
            <Button
              variant="contained"
              size="large"
              endIcon={<ArrowForwardIcon />}
              onClick={() => navigate(lp("/our-medicines"))}
              sx={{
                bgcolor: colors.accent,
                borderRadius: radii.md,
                px: 3.25,
                py: 1.4,
                fontWeight: 700,
                textTransform: "none",
                boxShadow: "none",
                "&:hover": { bgcolor: colors.accentDark, boxShadow: "none" },
              }}
            >
              Shop Medicines
            </Button>
            <Button
              variant="outlined"
              size="large"
              onClick={handleHowToOrder}
              sx={{
                borderColor: colors.primary,
                color: colors.primary,
                borderRadius: radii.md,
                px: 3.25,
                py: 1.4,
                fontWeight: 700,
                textTransform: "none",
                borderWidth: "1.5px",
                "&:hover": {
                  borderColor: colors.primary,
                  borderWidth: "1.5px",
                  bgcolor: colors.primaryLight,
                },
              }}
            >
              How to Order
            </Button>
          </Stack>

          <Grid container spacing={2.5} sx={{ mt: 3 }}>
            {badges.map((b, i) => (
              <Grid item key={i} sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
                <Box
                  sx={{
                    width: 38,
                    height: 38,
                    borderRadius: radii.md,
                    bgcolor: colors.white,
                    border: `1px solid ${colors.line}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: colors.primary,
                    flexShrink: 0,
                  }}
                >
                  <b.icon fontSize="small" />
                </Box>
                <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: colors.ink2, lineHeight: 1.3 }}>
                  {b.label}
                </Typography>
              </Grid>
            ))}
          </Grid>
        </Grid>

        <Grid item xs={12} md={6}>
          <Box
            sx={{
              position: "relative",
              height: { xs: 300, md: 380 },
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                width: 300,
                height: 300,
                borderRadius: "50%",
                background: `radial-gradient(circle, ${colors.primaryLight} 0%, transparent 70%)`,
              }}
            />
            <Box
              sx={{
                position: "relative",
                width: "100%",
                maxWidth: 340,
                height: 320,
              }}
            >
              <Box
                sx={{
                  ...cardSx,
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: 190,
                  p: 2,
                }}
              >
                <Stack direction="row" spacing={1.25} alignItems="center">
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: radii.md,
                      bgcolor: colors.primaryLight,
                      color: colors.primary,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <LocalShippingOutlinedIcon fontSize="small" />
                  </Box>
                  <Box>
                    <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: colors.ink }}>
                      Order Confirmed
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: colors.ink3 }}>Arriving in 2 days</Typography>
                  </Box>
                </Stack>
              </Box>

              <Box
                sx={{
                  position: "absolute",
                  top: 120,
                  left: 170,
                  width: 120,
                  height: 120,
                  borderRadius: "28px",
                  background: `linear-gradient(150deg, ${colors.primary}, ${colors.primaryDark})`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  boxShadow: shadows.card,
                }}
              >
                <MedicationOutlinedIcon sx={{ fontSize: 52, color: colors.white }} />
              </Box>

              <Box
                sx={{
                  ...cardSx,
                  position: "absolute",
                  bottom: 0,
                  left: 0,
                  width: 170,
                  p: 2,
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
                  <VerifiedOutlinedIcon sx={{ fontSize: 16, color: colors.primary }} />
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: colors.ink }}>
                    Pharmacist Verified
                  </Typography>
                </Stack>
                <Typography sx={{ fontSize: 11.5, color: colors.ink3, lineHeight: 1.5 }}>
                  Every prescription is reviewed before dispatch.
                </Typography>
              </Box>
            </Box>
          </Box>
        </Grid>
      </Grid>
      </Box>
    </Box>
  );
}

const cardSx = {
  bgcolor: colors.white,
  borderRadius: radii.lg,
  boxShadow: shadows.card,
  border: `1px solid ${colors.line}`,
};
